/**
 * ПОРОГИ ПРАВ (m.room.power_levels) — чтение и точечная запись для страницы комнаты.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, а не поля в roomActions: setPowerLevel там меняет
 * ОДНОГО пользователя и имеет собственные правила безопасности (запрет
 * понижения себя). Здесь другая задача — менять ЧИСЛОВЫЕ ПОРОГИ действий
 * (кто может писать/приглашать/менять настройки/удалять чужие сообщения)
 * для ВСЕХ ролей сразу. Смешать их в одной функции значит ослабить проверки
 * обеих.
 *
 * ПРАВИЛО БЕЗОПАСНОСТИ — то же, что у setPowerLevel в roomActions.ts:
 * content отправляется ЦЕЛИКОМ. Читаем текущий state, меняем ОДИН ключ,
 * отправляем обратно всё, включая незнакомые нам поля (notifications,
 * users, исторические расширения). Собрать content с нуля — значит молча
 * сбросить все прочие настройки комнаты на дефолты спеки.
 *
 * ОГРАНИЧЕНИЕ ОСОЗНАННОЕ: понижение порога ниже МОЕГО текущего уровня
 * возможно (админ может разрешить писать модераторам), повышение выше моего
 * уровня сервер отклонит сам — мы его не блокируем локально, потому что
 * ошибка сервера честнее нашей догадки о будущем state.
 */

import { EventType, type MatrixClient, type Room } from "matrix-js-sdk";

import { withRateLimit } from "./roomActions";

/** Сырой content m.room.power_levels (или undefined, если state ещё не пришёл). */
export function powerLevelsOf(room: Room): Record<string, unknown> | undefined {
  return room.currentState
    .getStateEvents(EventType.RoomPowerLevels, "")
    ?.getContent() as Record<string, unknown> | undefined;
}

/**
 * Числовой порог действия из content.
 *
 * Порядок по спеке: для сообщений — events[type] ?? events_default ?? 0;
 * для модераторских ключей (invite/redact) — сам ключ ?? fallback 50.
 * Вынесено сюда, чтобы UI показывал РОВНО те числа, по которым решит сервер,
 * а не свои догадки.
 */
export function thresholdOf(
  pl: Record<string, unknown> | undefined,
  key: "m.room.message" | "invite" | "redact" | "m.room.power_levels",
): number {
  if (key === "invite" || key === "redact") {
    const v = pl?.[key];
    return typeof v === "number" ? v : 50;
  }
  const events = (pl?.events ?? {}) as Record<string, number>;
  const v = events[key];
  if (typeof v === "number") return v;
  const d = pl?.events_default;
  if (typeof d === "number") return d;
  return key === "m.room.message" ? 0 : 50;
}

/**
 * Изменить ОДИН порог.
 *
 * key:
 *  - "m.room.message"     — кто может писать (events);
 *  - "invite"             — кто может приглашать (верхний уровень content);
 *  - "redact"             — кто может удалять чужие сообщения (верхний уровень);
 *  - "m.room.power_levels" — кто может менять сами права (events).
 *
 * Для events-ключей значение кладётся в events[key]; для invite/redact —
 * в корень content. Другие поля не трогаем никогда.
 */
export async function setPowerLevelThreshold(
  client: MatrixClient,
  room: Room,
  key: "m.room.message" | "invite" | "redact" | "m.room.power_levels",
  level: number,
): Promise<void> {
  const current = powerLevelsOf(room) ?? {};
  let next: Record<string, unknown>;
  if (key === "invite" || key === "redact") {
    next = { ...current, [key]: level };
  } else {
    const events = { ...((current.events as Record<string, number> | undefined) ?? {}) };
    events[key] = level;
    next = { ...current, events };
  }
  await withRateLimit(() =>
    client.sendStateEvent(room.roomId, EventType.RoomPowerLevels, next),
  );
}
